// AddPropertyDrawer — premium multi-stage property onboarding wizard.
// Stages: 1 Property Details → 2 Vendor → 3 Compliance → 4 Marketing → 5 Launch Review
// Plus a post-completion "What's next?" action card screen.
// Keeps the same external API: cf:open-property event, with `_editing` flag for edit mode.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, CheckCircle2, ImagePlus, Search, User, Sparkles, AlertTriangle,
  Loader2, BedDouble, Bath, MapPin, ShieldCheck, Megaphone, Rocket, FileText, Pencil,
  Calendar, Upload, Building2, Home, PoundSterling, UserCheck, Image as ImageIcon,
  Locate, MapPinned,
} from "lucide-react";
import { Drawer } from "../primitives/Modal";
import { useChainStore, cfBus, openAddContact, openCreateDeal } from "../../data/chainStore";
import { initialsOf } from "../contacts/AddContactModal";
import { apiError, apiErrorCode, apiErrorField } from "../../lib/api";
import { formatAgreedFee, resolveAgreedFeeGBP } from "../../lib/feeFormat";
import { PremiumInput } from "../brand/PremiumInput";
import { PremiumSelect } from "../brand/PremiumSelect";
import { PremiumTextarea } from "../brand/PremiumTextarea";
import { useLeadNegotiators } from "./useLeadNegotiators";

const TYPES = ["Detached house", "Semi-detached", "Semi-detached house", "Terraced", "Terraced house", "Apartment", "Maisonette", "New build", "Penthouse", "Bungalow"];
const STATUSES = ["Draft", "Listed", "Under Offer", "Sales Agreed", "Exchanged", "Completed", "Withdrawn"];
const TENURES = ["Freehold", "Leasehold", "Share of Freehold", "Commonhold"];
// iter-259 — Lightweight operational parking taxonomy. Surfaces in
// the Property page card chip, Property modal hero, and Deal Room
// property summary. Not a marketing field — just enough metadata
// for an operator to scan parking provision at a glance.
const EWS1_RATINGS = ["A1", "A2", "A3", "B1", "B2"];
const PARKING_OPTIONS = ["None", "On-street", "Allocated", "Garage", "Underground", "Residents permit", "Driveway"];

const STAGES = [
  { id: 1, label: "Property Details", icon: Building2, eyebrow: "Step 1 of 5" },
  { id: 2, label: "Vendor Information", icon: User,    eyebrow: "Step 2 of 5" },
  { id: 3, label: "Compliance",        icon: ShieldCheck, eyebrow: "Step 3 of 5" },
  { id: 4, label: "Marketing Setup",   icon: Megaphone, eyebrow: "Step 4 of 5" },
  { id: 5, label: "Launch Review",     icon: Rocket,    eyebrow: "Step 5 of 5" },
];

// ─── Validation per stage ────────────────────────────────────────────────────
// Numeric helpers — accept "725000", "£725,000", "725,000" etc.
const numericClean = (v) => String(v || "").replace(/[^0-9]/g, "");
const isPositiveInt = (v) => {
  const n = numericClean(v);
  return n.length > 0 && parseInt(n, 10) > 0;
};

const stage1Errors = (f) => {
  const e = {};
  if (!f.address?.trim())   e.address  = "Required";
  if (!f.postcode?.trim())  e.postcode = "Required";
  if (!f.type)              e.type     = "Required";
  if (!isPositiveInt(f.beds))   e.beds   = "Required";
  if (!isPositiveInt(f.baths))  e.baths  = "Required";
  if (!isPositiveInt(f.price))  e.price  = "Required";
  if (!f.tenure)            e.tenure   = "Required";
  return e;
};
const stage2Errors = (f) => {
  const e = {};
  if (!f.sellerId) e.sellerId = "Select or add a vendor to continue";
  return e;
};
const stage3Errors = (_f) => ({}); // tenure now lives on stage 1
const stage4Errors = (_f) => ({});
const stageValidator = { 1: stage1Errors, 2: stage2Errors, 3: stage3Errors, 4: stage4Errors };

const initialForm = (preset = {}) => ({
  address: "", postcode: "", locality: "", type: "", beds: "3", baths: "2", price: "",
  sellerId: null, agentId: null, office: "", status: "Draft",
  tenure: "Freehold", leaseYears: "", svc: "", rent: "", epc: "", aml: "", notes: "", thumb: "",
  // iter-259 — Lightweight operational property metadata.
  parking: "None", parkingSpaces: "",
  description: "", viewingAvailability: "", photographyBooked: false,
  // iter-alignment · P3 · Properties · Commission Terms (2026-02-28)
  // These fields flow to the Property SSOT and get inherited by
  // the Deal Room on creation.  Defaults land the operator on
  // the industry-standard percentage model so the "add" flow
  // stays fast without forcing a decision.
  commissionType: "percentage",   // "percentage" | "fixed"
  commissionPct: "1.25",          // % of asking price
  fixedFee: "",                   // £ literal
  commissionVatIncluded: false,
  // iter-alignment · P0 · Property Edit Drawer — Compact Redesign
  // (2026-02-28) — Building Safety Act Yes/No.  Undefined = unset,
  // matches the tri-state on the Property SSOT.
  bsaApplies: undefined,
  ...preset,
});

// ─── Completeness scoring ────────────────────────────────────────────────────
const SCORE_FIELDS = [
  { key: "address",     weight: 10 },
  { key: "postcode",    weight: 6 },
  { key: "type",        weight: 6 },
  { key: "price",       weight: 8 },
  { key: "beds",        weight: 4 },
  { key: "baths",       weight: 4 },
  { key: "thumb",       weight: 8 },
  { key: "sellerId",    weight: 12 },
  { key: "agentId",     weight: 6 },
  { key: "tenure",      weight: 6 },
  { key: "epc",         weight: 6 },
  { key: "aml",         weight: 4 },
  { key: "description", weight: 8 },
  { key: "photographyBooked", weight: 6 },
  { key: "viewingAvailability", weight: 6 },
];

const computeScore = (f) => {
  let total = 0;
  SCORE_FIELDS.forEach(({ key, weight }) => {
    const v = f[key];
    if (typeof v === "boolean") { if (v) total += weight; }
    else if (typeof v === "string") { if (v.trim()) total += weight; }
    else if (v != null && v !== "") { total += weight; }
  });
  return Math.min(100, Math.round(total));
};

// ─── Tiny presentational helpers ─────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, label, hint }) => (
  <div className="flex items-start gap-3 pb-4 mb-5 border-b border-slate-100">
    <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
      <Icon size={16} strokeWidth={2.2} />
    </div>
    <div className="min-w-0">
      <h3 className="cf-h-section text-[15px] leading-snug">{label}</h3>
      {hint && <p className="text-[12px] text-slate-500 mt-0.5">{hint}</p>}
    </div>
  </div>
);

const StageDots = ({ current, total = 5 }) => {
  const pct = Math.round((current / total) * 100);
  return (
    <div className="hidden sm:flex flex-col items-end gap-1.5 shrink-0" data-testid="property-progress">
      <p className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-bold">Property Setup</p>
      <div className="flex items-center gap-1.5">
        {Array.from({ length: total }).map((_, i) => (
          <span
            key={i}
            className={`w-2 h-2 rounded-full transition-all duration-500 ${
              i < current ? "bg-indigo-600 scale-110 shadow-[0_0_0_3px_rgba(99, 102, 241,0.18)]" : "bg-slate-200"
            }`}
          />
        ))}
      </div>
      <p className="text-[10.5px] text-slate-500 cf-num"><span className="font-bold text-slate-900">{pct}%</span> Complete</p>
    </div>
  );
};

// Subtle slide+fade for stage transitions, keyed by step.
const StagePane = ({ step, children }) => (
  <div key={step} className="cf-fade-up">{children}</div>
);

// ─── Vendor picker (kept similar to original, slightly cleaner) ──────────────
const VendorSearch = ({ value, onChange }) => {
  const { contacts } = useChainStore();
  const [q, setQ] = useState("");
  const candidates = contacts.filter((c) => ["Seller", "Buyer", "Estate Agent"].includes(c.role)
    && !(c.role === "Estate Agent" && (c.tags || []).includes("Staff")));
  const list = q ? candidates.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) || (c.company || "").toLowerCase().includes(q.toLowerCase())) : candidates.slice(0, 5);
  const selected = contacts.find((c) => c.id === value);

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-indigo-50/40" data-testid="vendor-selected">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 text-white text-[12px] font-bold flex items-center justify-center cf-shadow-md">{initialsOf(selected.name)}</div>
          <div className="min-w-0">
            <p className="text-[13.5px] font-bold text-slate-900 truncate">{selected.name}</p>
            <p className="text-[11.5px] text-slate-500 truncate">{selected.role}{selected.company ? ` · ${selected.company}` : ""}</p>
          </div>
        </div>
        <button onClick={() => onChange(null)} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-900" data-testid="vendor-change">Change</button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input className="h-10 pl-9 pr-3 w-full text-[13px] text-slate-800 placeholder:text-slate-400 bg-white outline-none border-0" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search seller contacts…" data-testid="property-vendor-search" />
      </div>
      <div className="max-h-44 overflow-y-auto cf-scroll border-t border-slate-100">
        {list.map((c) => (
          <button key={c.id} onClick={() => onChange(c.id)} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-indigo-50 transition-colors text-left" data-testid={`property-vendor-${c.id}`}>
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-700 text-white text-[10.5px] font-bold flex items-center justify-center">{initialsOf(c.name)}</div>
            <div className="min-w-0">
              <p className="text-[12.5px] font-semibold text-slate-900 truncate">{c.name}</p>
              <p className="text-[10.5px] text-slate-500 truncate">{c.role}{c.company ? ` · ${c.company}` : ""}</p>
            </div>
          </button>
        ))}
        {list.length === 0 && <p className="px-3 py-3 text-[11.5px] text-slate-400">No matches.</p>}
      </div>
      <button onClick={() => openAddContact({ role: "Seller" })} className="w-full px-3 py-2.5 text-[11.5px] font-semibold text-indigo-700 hover:bg-indigo-50 border-t border-slate-100 inline-flex items-center justify-center gap-1.5" data-testid="vendor-add-new">
        <User size={11} /> + Add new vendor
      </button>
    </div>
  );
};

// ─── Insights panel (right rail) ─────────────────────────────────────────────
const InsightsPanel = ({ form, score, errors, currentStep, properties }) => {
  const isLeasehold = form.tenure === "Leasehold" || form.tenure === "Share of Freehold";
  const matchType = form.type;
  const similar = properties.filter((p) => p.type === matchType && p.id !== form.id).slice(0, 3);

  const launchChecklist = [
    { ok: !!form.thumb,                        label: "Hero photograph uploaded" },
    { ok: !!form.sellerId,                     label: "Vendor linked" },
    { ok: !!form.epc,                          label: "EPC rating recorded" },
    { ok: !!form.aml || form.tenure !== "Leasehold", label: isLeasehold ? "AML / lease pack started" : "AML noted (freehold)" },
    { ok: !!form.description?.trim(),          label: "Listing description drafted" },
    { ok: form.photographyBooked,              label: "Photography scheduled" },
  ];
  const launchPct = Math.round((launchChecklist.filter((x) => x.ok).length / launchChecklist.length) * 100);

  // Suggested next actions based on current stage
  const suggestions = [];
  if (!form.thumb) suggestions.push("Upload a hero photo — listings with photography convert 4× faster");
  if (currentStep >= 3 && !form.epc) suggestions.push("EPC rating still missing — request from vendor or order survey");
  if (isLeasehold && currentStep >= 3 && !form.aml) suggestions.push("Leasehold flagged — start AML pack and request lease information form");
  if (currentStep >= 4 && !form.description?.trim()) suggestions.push("Draft a 2-line listing description before launch");
  if (currentStep >= 4 && !form.photographyBooked) suggestions.push("Lock in a photography slot — Tuesday afternoons are quickest");

  return (
    <div className="p-6 space-y-7">
      {/* Completeness ring */}
      <div className="text-center" data-testid="property-completeness">
        <p className="cf-eyebrow cf-eyebrow-emerald mb-3">Property Completeness</p>
        <div className="relative w-28 h-28 mx-auto">
          <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
            <circle cx="60" cy="60" r="52" stroke="#EEF2FF" strokeWidth="10" fill="none" />
            <circle
              cx="60" cy="60" r="52" stroke="url(#cf-grad)" strokeWidth="10" fill="none" strokeLinecap="round"
              strokeDasharray={`${(score / 100) * 326.7} 326.7`}
              style={{ transition: "stroke-dasharray 600ms cubic-bezier(0.16, 1, 0.3, 1)" }}
            />
            <defs>
              <linearGradient id="cf-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#6366F1" />
                <stop offset="100%" stopColor="#6366F1" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-display text-[28px] font-bold text-slate-900 cf-num">{score}<span className="text-[14px] text-slate-500">%</span></span>
          </div>
        </div>
        <p className="text-[11.5px] text-slate-500 mt-2">{score >= 80 ? "Ready to launch" : score >= 50 ? "Almost there" : "Just getting started"}</p>
      </div>

      {/* Launch readiness checklist */}
      <div data-testid="property-launch-readiness">
        <p className="cf-eyebrow mb-3">Launch readiness · <span className="text-indigo-700 cf-num">{launchPct}%</span></p>
        <ul className="space-y-2">
          {launchChecklist.map((c) => (
            <li key={c.label} className={`flex items-start gap-2 text-[12px] ${c.ok ? "text-slate-700" : "text-slate-400"}`}>
              <span className={`w-4 h-4 rounded-full flex items-center justify-center mt-0.5 shrink-0 ${c.ok ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400 border border-dashed border-slate-300"}`}>
                {c.ok ? <CheckCircle2 size={10} strokeWidth={2.5} /> : null}
              </span>
              <span className="leading-tight">{c.label}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Suggested next actions */}
      {suggestions.length > 0 && (
        <div data-testid="property-suggestions">
          <p className="cf-eyebrow cf-eyebrow-emerald mb-2 inline-flex items-center gap-1.5"><Sparkles size={11} /> Suggestions</p>
          <ul className="space-y-2">
            {suggestions.slice(0, 3).map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-[11.5px] text-slate-700 px-3 py-2 rounded-lg bg-indigo-50/60 border border-indigo-100">
                <Sparkles size={10} className="text-indigo-500 mt-0.5 shrink-0" />
                <span className="leading-snug">{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Similar listings */}
      {similar.length > 0 && (
        <div data-testid="property-similar">
          <p className="cf-eyebrow mb-2">Similar recent listings</p>
          <ul className="space-y-2">
            {similar.map((p) => (
              <li key={p.id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-slate-100 overflow-hidden border border-slate-200 shrink-0">
                  {p.thumb && <img src={p.thumb} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0">
                  <p className="text-[12px] font-semibold text-slate-900 truncate">{p.address?.split(",")[0]}</p>
                  <p className="text-[10.5px] text-slate-500 cf-num">{p.price} · {p.beds} bed</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Validation summary */}
      {Object.keys(errors).length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="property-validation-summary">
          <p className="cf-eyebrow text-amber-800 mb-1.5 inline-flex items-center gap-1.5"><AlertTriangle size={11} /> To continue</p>
          <p className="text-[11.5px] text-amber-800/80 leading-snug">{Object.values(errors).join(" · ")}</p>
        </div>
      )}
    </div>
  );
};

// ─── Review summary (stage 5 + post-save success) ───────────────────────────
const ReviewSummary = ({ form, contacts, score, onJump }) => {
  const seller = contacts.find((c) => c.id === form.sellerId);
  const agent = contacts.find((c) => c.id === form.agentId);
  const isLeasehold = form.tenure === "Leasehold" || form.tenure === "Share of Freehold";

  const Row = ({ label, value, edit, testid }) => (
    <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0">
      <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold w-32 shrink-0 mt-0.5">{label}</p>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-slate-900">{value || <span className="text-slate-400">—</span>}</p>
      </div>
      {edit != null && onJump && (
        <button onClick={() => onJump(edit)} className="text-[11px] font-semibold text-indigo-700 hover:text-indigo-800 inline-flex items-center gap-1 shrink-0" data-testid={testid}>
          <Pencil size={10} /> Edit
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="property-review">
      {/* Hero card */}
      <div className="rounded-2xl overflow-hidden border border-slate-200 cf-shadow">
        <div className="aspect-[16/8] bg-slate-100 relative">
          {form.thumb ? <img src={form.thumb} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><ImagePlus size={28} /></div>}
          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[10.5px] font-bold uppercase tracking-[0.14em] bg-white/95 text-slate-700 border border-white/40">{form.status}</span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[10.5px] font-bold uppercase tracking-[0.14em] bg-indigo-600 text-white">{score}% Ready</span>
          </div>
        </div>
        <div className="p-5">
          <h3 className="cf-h-section text-[16px]">{form.address?.split(",")[0] || "Property address"}</h3>
          <p className="text-[12.5px] text-slate-500 mt-1 inline-flex items-center gap-1.5"><MapPin size={11} /> {form.address || "—"}</p>
          <div className="flex items-center gap-4 mt-3 text-[12px] text-slate-700">
            <span className="inline-flex items-center gap-1"><BedDouble size={12} /> {form.beds || "—"}</span>
            <span className="inline-flex items-center gap-1"><Bath size={12} /> {form.baths || "—"}</span>
            <span className="text-slate-400">·</span>
            <span>{form.type || "—"}</span>
            <span className="ml-auto font-display text-[20px] font-bold cf-num text-indigo-700">{form.price || "—"}</span>
          </div>
        </div>
      </div>

      {/* Detail rows */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <Row label="Vendor"            value={seller ? `${seller.name}${seller.company ? ` · ${seller.company}` : ""}` : null} edit={2} testid="review-edit-vendor" />
        <Row label="Lead negotiator"   value={agent?.name} edit={2} testid="review-edit-agent" />
        <Row label="Tenure"            value={form.tenure} edit={3} testid="review-edit-tenure" />
        <Row label="EPC rating"        value={form.epc} edit={3} testid="review-edit-epc" />
        <Row label="Service charge"    value={form.svc} edit={3} testid="review-edit-svc" />
        <Row label="Compliance"        value={isLeasehold ? (form.aml ? "AML pack started" : "AML pending — leasehold flagged") : "Standard freehold"} edit={3} testid="review-edit-aml" />
        <Row label="Listing copy"      value={form.description ? `${form.description.slice(0, 80)}${form.description.length > 80 ? "…" : ""}` : null} edit={4} testid="review-edit-description" />
        <Row label="Photography"       value={form.photographyBooked ? "Scheduled" : "Not yet booked"} edit={4} testid="review-edit-photography" />
        <Row label="Viewing windows"   value={form.viewingAvailability} edit={4} testid="review-edit-viewings" />
      </div>
    </div>
  );
};

// ─── Post-save action cards ──────────────────────────────────────────────────
const NextActionCards = ({ savedProp, close }) => {
  const cards = [
    { id: "deal",      label: "Start Transaction",      hint: "Open a deal room and start tracking progression",       icon: ArrowRight, action: () => { close(); cfBus.emit("cf:open-deal", { propertyId: savedProp.id, sellerId: savedProp.sellerId }); } },
    { id: "invite",    label: "Invite Seller",          hint: "Send a portal invite so the vendor stays informed",    icon: User,       action: () => { close(); toast.info("Seller invite", { description: "Open the deal room to send a portal invite." }); } },
    { id: "documents", label: "Upload Documents",       hint: "Drop in EPC, lease pack, floor plans and photography",  icon: Upload,     action: () => { close(); window.location.assign("/documents"); } },
    { id: "viewing",   label: "Add Viewing Availability", hint: "Block out windows for first viewings this week",    icon: Calendar,   action: () => { close(); toast.info("Viewings", { description: "Open the property in Deal Rooms to add viewing slots." }); } },
  ];
  return (
    <div className="space-y-6 cf-fade-up" data-testid="property-next-actions">
      <div className="text-center py-2">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 mx-auto flex items-center justify-center cf-shadow-md">
          <CheckCircle2 size={26} className="text-white" strokeWidth={2.4} />
        </div>
        <h3 className="cf-h-section mt-4">{savedProp.address.split(",")[0]} is live</h3>
        <p className="text-[13px] text-slate-500 mt-1.5">What would you like to do next?</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cards.map((c) => (
          <button
            key={c.id}
            onClick={c.action}
            className="group text-left rounded-2xl border border-slate-200 bg-white hover:border-indigo-300 hover:cf-shadow-md transition-all p-4 flex items-start gap-3"
            data-testid={`next-${c.id}`}
          >
            <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-colors shrink-0">
              <c.icon size={16} strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <p className="text-[13.5px] font-bold text-slate-900 group-hover:text-indigo-700 transition-colors">{c.label}</p>
              <p className="text-[11.5px] text-slate-500 mt-0.5 leading-snug">{c.hint}</p>
            </div>
            <ArrowRight size={14} className="text-slate-300 group-hover:text-indigo-600 group-hover:translate-x-0.5 transition-all ml-auto mt-1" />
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── iter-273 · Single-screen Create Property experience ────────────────────
//
// Refinement of the legacy 5-stage wizard. Renders a focused, premium
// one-screen form covering only the essential fields an estate-agency
// operator needs to start a transaction:
//   Address · Postcode · Property Type · Asking Price · Bedrooms ·
//   Bathrooms · Tenure · Negotiator · Vendor (optional) · Hero Image (optional)
//
// Operational discipline:
//   • Reuses the same `form` state and `addProperty` save path as the
//     legacy wizard — no functional regressions.
//   • Right rail shows Property Preview + 4-step Next Steps only.
//     No marketing / help / insights cards.
//   • Dual CTA pattern in the footer — Primary "Create & Start
//     Transaction" creates the property then opens the Create Deal
//     flow with the new property pre-selected. Secondary "Create
//     Property" saves only.
//   • Mobile responsive — grid collapses to single column < md and
//     the right rail moves below the form via `aside`-stack semantics
//     provided by the Drawer primitive.

const quickCreateErrors = (f) => {
  const e = {};
  if (!f.address?.trim())   e.address  = "Required";
  if (!f.postcode?.trim())  e.postcode = "Required";
  if (!f.type)              e.type     = "Required";
  if (!isPositiveInt(f.price)) e.price = "Required";
  if (!isPositiveInt(f.beds))  e.beds  = "Required";
  if (!f.tenure)            e.tenure   = "Required";
  if (!f.agentId)           e.agentId  = "Required";
  return e;
};

const QUICK_PROPERTY_TYPES = [
  "Detached house", "Semi-detached", "Terraced", "Apartment",
  "New build", "Penthouse", "Bungalow",
];

/* Property preview + next steps rail.
 * Drives the right column. Reads strictly from the live form so the
 * preview updates as the operator types. Next Steps are deterministic
 * four-item list mirroring the reference design. */
const NextStepsRail = ({ form, contacts, score, isLeasehold }) => {
  const negotiator = contacts.find((c) => c.id === form.agentId);
  const vendor = contacts.find((c) => c.id === form.sellerId);
  const addrFirstLine = (form.address || "").split(",")[0]?.trim();
  const addrTail = (form.address || "").split(",").slice(1).join(",").trim();
  const fmtPrice = (raw) => {
    const n = parseInt(numericClean(raw), 10);
    if (!Number.isFinite(n) || n <= 0) return null;
    return `£${n.toLocaleString("en-GB")}`;
  };
  const priceLabel = fmtPrice(form.price);

  const steps = [
    {
      id: 1,
      title: "Create Deal Room",
      hint: "Set up this property's transaction",
      done: false,
    },
    {
      id: 2,
      title: "Add Vendor",
      hint: vendor ? `${vendor.name} linked` : "Add the property owner",
      done: !!vendor,
    },
    {
      id: 3,
      title: "Invite Participants",
      hint: "Buyers, solicitors, brokers",
      done: false,
    },
    {
      id: 4,
      title: "Upload Hero Image",
      hint: form.thumb ? "Image uploaded" : "Make your listing stand out",
      done: !!form.thumb,
    },
  ];

  const completePct = Math.min(100, score);
  const readyTone =
    completePct >= 60
      ? { tone: "emerald", label: "Ready to Market" }
      : completePct >= 30
        ? { tone: "amber",   label: "Almost ready" }
        : { tone: "slate",   label: "Just started" };
  const TONE_CLASSES = {
    emerald: { dot: "bg-emerald-500", text: "text-emerald-700" },
    amber:   { dot: "bg-amber-500",   text: "text-amber-700" },
    slate:   { dot: "bg-slate-400",   text: "text-slate-600" },
  };
  const tone = TONE_CLASSES[readyTone.tone];

  return (
    <div className="space-y-5" data-testid="quick-create-rail">
      <section
        className="rounded-2xl border border-slate-200/80 bg-white p-5 cf-shadow-sm"
        data-testid="quick-create-preview"
      >
        <div className="flex items-center justify-between mb-4">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500">
            Property Preview
          </p>
          <span
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:text-indigo-900 cursor-default"
            data-testid="quick-create-preview-edit"
            title="Edit any field in the form on the left"
          >
            <Pencil size={11} /> Edit preview
          </span>
        </div>
        <div className="aspect-[4/3] rounded-xl overflow-hidden border border-slate-100 bg-slate-50 mb-4">
          {form.thumb ? (
            <img
              src={form.thumb}
              alt={addrFirstLine || "Property"}
              className="w-full h-full object-cover"
              data-testid="quick-create-preview-image"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-slate-300" data-testid="quick-create-preview-empty">
              <ImageIcon size={28} strokeWidth={1.6} />
              <p className="text-[10.5px] uppercase tracking-[0.18em] mt-2 font-semibold">
                Hero image
              </p>
            </div>
          )}
        </div>
        <p className="text-[16px] font-bold text-slate-900 truncate" data-testid="quick-create-preview-address">
          {addrFirstLine || "Property address"}
        </p>
        {addrTail && (
          <p className="text-[12.5px] text-slate-500 truncate mt-0.5">{addrTail}</p>
        )}
        {form.postcode && (
          <p className="text-[12.5px] text-slate-500 mt-0.5">{String(form.postcode).toUpperCase()}</p>
        )}
        <div className="mt-4 flex items-center justify-between pt-4 border-t border-slate-100">
          <div className="inline-flex items-center gap-1.5 text-[12px] text-slate-600">
            <ShieldCheck size={12} className="text-slate-400" />
            <span>{form.tenure || "Tenure"}</span>
          </div>
          {priceLabel && (
            <span className="text-[15px] font-bold text-indigo-700 cf-num" data-testid="quick-create-preview-price">
              {priceLabel}
            </span>
          )}
        </div>
        <div className="mt-3 flex items-center gap-4 text-[12px] text-slate-600">
          <span className="inline-flex items-center gap-1.5">
            <BedDouble size={12} className="text-slate-400" />
            {form.beds || "—"} Bed
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Bath size={12} className="text-slate-400" />
            {form.baths || "—"} Bath
          </span>
        </div>
        <div className="mt-4 rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5" data-testid="quick-create-preview-status">
          <div className="flex items-center justify-between text-[11.5px]">
            <span className={`inline-flex items-center gap-1.5 font-semibold ${tone.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
              {readyTone.label}
            </span>
            <span className="text-slate-500 cf-num">{completePct}% complete</span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-slate-200/80 overflow-hidden">
            <div
              className={`h-full rounded-full ${
                readyTone.tone === "emerald" ? "bg-emerald-500"
                : readyTone.tone === "amber" ? "bg-amber-500"
                : "bg-slate-400"
              }`}
              style={{ width: `${completePct}%` }}
            />
          </div>
        </div>
        {isLeasehold && (
          <p className="mt-3 text-[10.5px] text-amber-700 inline-flex items-center gap-1.5">
            <AlertTriangle size={11} /> Leasehold — start the lease pack early.
          </p>
        )}
      </section>

      <section
        className="rounded-2xl border border-slate-200/80 bg-white p-5 cf-shadow-sm"
        data-testid="quick-create-next-steps"
      >
        <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">
          Next Steps
        </p>
        <ol className="space-y-3">
          {steps.map((s) => (
            <li
              key={s.id}
              className="flex items-start gap-3 group"
              data-testid={`quick-create-next-step-${s.id}`}
            >
              <span
                className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[12px] font-bold cf-num ${
                  s.done
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-indigo-50 text-indigo-700"
                }`}
              >
                {s.done ? <CheckCircle2 size={14} /> : s.id}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-bold text-slate-900 truncate">{s.title}</p>
                <p className="text-[11.5px] text-slate-500 truncate leading-tight mt-0.5">{s.hint}</p>
              </div>
              <ArrowRight size={13} className="text-slate-300 group-hover:text-indigo-500 transition-colors mt-1.5 shrink-0" />
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
};

/* Single-screen Create Property form body. The 9 fields the spec
 * keeps + an optional hero image uploader. Mirrors the reference
 * screenshot exactly while reusing the existing PremiumInput /
 * PremiumSelect / VendorSearch primitives so the visual system
 * stays consistent. */
const QuickCreateBody = ({
  form, set, errors, contacts, onThumb, onLookupAddress, onFindAddress, lastSavedLabel, negotiators, onPickNegotiator,
}) => {
  return (
    <div className="space-y-6 cf-fade-up" data-testid="quick-create-body">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-indigo-600" data-testid="quick-create-eyebrow">
          Essential Details
        </p>
        {lastSavedLabel && (
          <span className="text-[10.5px] text-slate-400 inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 cf-pulse" />
            {lastSavedLabel}
          </span>
        )}
      </div>

      {/* Address + lookup */}
      <div className="grid grid-cols-12 gap-x-4 gap-y-2 items-end">
        <div className="col-span-12 sm:col-span-8 lg:col-span-9">
          <PremiumInput
            label="Address"
            required
            value={form.address}
            onChange={(v) => set("address", v)}
            placeholder="12 Mersey Reach, Aigburth, Liverpool"
            error={errors.address}
            autoFocus
            testid="property-address"
          />
        </div>
        <div className="col-span-12 sm:col-span-4 lg:col-span-3">
          <button
            type="button"
            onClick={onFindAddress}
            className="h-10 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50/40 text-[12.5px] font-semibold text-indigo-700 hover:bg-indigo-50 transition-colors"
            data-testid="quick-create-find-address"
          >
            <Locate size={13} /> Find Address
          </button>
        </div>
      </div>

      {/* Postcode + lookup */}
      <div className="grid grid-cols-12 gap-x-4 gap-y-2 items-end">
        <div className="col-span-12 sm:col-span-8 lg:col-span-9">
          <PremiumInput
            label="Postcode"
            required
            value={form.postcode}
            onChange={(v) => set("postcode", String(v).toUpperCase())}
            placeholder="L17 6AS"
            maxLength={9}
            autoComplete="postal-code"
            error={errors.postcode}
            testid="property-postcode"
          />
        </div>
        <div className="col-span-12 sm:col-span-4 lg:col-span-3">
          <button
            type="button"
            onClick={onLookupAddress}
            className="h-10 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50/40 text-[12.5px] font-semibold text-indigo-700 hover:bg-indigo-50 transition-colors"
            data-testid="quick-create-lookup-address"
          >
            <MapPinned size={13} /> Lookup Address
          </button>
        </div>
      </div>

      <PremiumInput
        label="Area / locality"
        value={form.locality || ""}
        onChange={(v) => set("locality", v)}
        placeholder="e.g. Islington"
        maxLength={120}
        testid="property-locality"
      />

      {/* Type + Price */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-6">
          <PremiumSelect
            label="Property type"
            required
            value={form.type}
            onChange={(v) => set("type", v)}
            placeholder="Select type…"
            error={errors.type}
            testid="property-type"
            options={QUICK_PROPERTY_TYPES.map((t) => ({ value: t, label: t }))}
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <PremiumInput
            label="Asking price"
            required
            format="currency"
            value={form.price}
            onChange={(v) => set("price", v)}
            placeholder="725,000"
            error={errors.price}
            testid="property-price"
          />
        </div>
      </div>

      {/* iter-alignment · P3 · Properties · Commission Terms (2026-02-28)
          Sits directly beneath Asking Price so the operator captures
          the commercial terms in one pass.  Reuses the SAME
          percentage / fixed vocabulary the Deal Room's FinancialsSheet
          uses — zero duplicate business logic downstream. */}
      <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4" data-testid="property-commission-section">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[13px] font-bold text-slate-900">Commission terms</p>
            <p className="text-[11.5px] text-slate-500 mt-0.5">
              Inherited by the Deal Room on creation. Editable per deal from the Revenue card.
            </p>
          </div>
        </div>

        <div
          className="inline-flex rounded-xl bg-white p-1 border border-slate-200 mb-3"
          role="tablist"
          data-testid="property-commission-mode"
        >
          {[
            { id: "percentage", label: "Percentage" },
            { id: "fixed",      label: "Fixed fee" },
          ].map((mode) => (
            <button
              key={mode.id}
              type="button"
              onClick={() => set("commissionType", mode.id)}
              className={`px-3 h-8 rounded-lg text-[12px] font-semibold transition-colors ${
                form.commissionType === mode.id
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid={`property-commission-mode-${mode.id}`}
              role="tab"
              aria-selected={form.commissionType === mode.id}
            >
              {mode.label}
            </button>
          ))}
        </div>

        {form.commissionType === "percentage" ? (
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-4">
              <PremiumInput
                label="Commission %"
                type="number"
                inputMode="decimal"
                value={form.commissionPct}
                onChange={(v) => set("commissionPct", v)}
                placeholder="1.25"
                testid="property-commission-pct"
                suffix="%"
              />
            </div>
            <div className="col-span-12 md:col-span-8">
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-1.5">
                Estimated fee
              </div>
              <div
                className="h-11 rounded-lg bg-white border border-slate-200 px-3 flex items-center text-[14px] font-semibold text-slate-900"
                data-testid="property-commission-estimate"
              >
                {(() => {
                  const priceNum = parseInt(numericClean(form.price || ""), 10);
                  const pctNum = parseFloat(String(form.commissionPct || "").replace(/,/g, ""));
                  if (!Number.isFinite(priceNum) || priceNum <= 0 || !Number.isFinite(pctNum) || pctNum <= 0) {
                    return <span className="text-slate-400 font-normal">Enter an asking price and %</span>;
                  }
                  const fee = resolveAgreedFeeGBP({ commissionType: "percentage", feePct: pctNum, agreedPrice: priceNum });
                  return (
                    <span>
                      {formatAgreedFee(fee).replace(/ \+ VAT$/, "")}
                      <span className="text-[11.5px] font-normal text-slate-500 ml-2">
                        · {pctNum}% of £{priceNum.toLocaleString("en-GB")}
                      </span>
                    </span>
                  );
                })()}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6">
              <PremiumInput
                label="Fixed fee"
                format="currency"
                value={form.fixedFee}
                onChange={(v) => set("fixedFee", v)}
                placeholder="9,500"
                testid="property-commission-fixed"
              />
            </div>
            <div className="col-span-12 md:col-span-6 flex items-end">
              <p
                className="text-[11.5px] text-slate-500 leading-snug"
                data-testid="property-commission-fixed-blurb"
              >
                Fixed fees pass through to the Deal Room unchanged, regardless of the agreed price.
              </p>
            </div>
          </div>
        )}

        <label
          className="mt-3 inline-flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"
          data-testid="property-commission-vat-label"
        >
          <input
            type="checkbox"
            className="w-4 h-4 accent-indigo-600"
            checked={!!form.commissionVatIncluded}
            onChange={(e) => set("commissionVatIncluded", e.target.checked)}
            data-testid="property-commission-vat"
          />
          <span>Fee is <strong className="font-semibold">inclusive</strong> of VAT</span>
        </label>
      </div>

      {/* Bedrooms + Bathrooms */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-6">
          <PremiumInput
            label="Bedrooms"
            required
            type="number"
            inputMode="numeric"
            value={form.beds}
            onChange={(v) => set("beds", v)}
            error={errors.beds}
            testid="property-beds"
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <PremiumInput
            label="Bathrooms"
            type="number"
            inputMode="numeric"
            value={form.baths}
            onChange={(v) => set("baths", v)}
            error={errors.baths}
            testid="property-baths"
          />
        </div>
      </div>

      {/* Tenure + Negotiator */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-6">
          <PremiumSelect
            label="Tenure"
            required
            value={form.tenure}
            onChange={(v) => set("tenure", v)}
            placeholder="Select tenure…"
            error={errors.tenure}
            testid="property-tenure"
            options={TENURES.map((s) => ({ value: s, label: s }))}
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <PremiumSelect
            label="Negotiator"
            required
            value={form.agentId || ""}
            onChange={onPickNegotiator}
            placeholder="Unassigned"
            error={errors.agentId}
            clearable
            searchable={negotiators.length > 6}
            testid="property-agent"
            options={negotiators}
          />
        </div>
      </div>

      {/* Vendor (optional) */}
      <div className="pt-2 border-t border-slate-100">
        <div className="mb-2">
          <label className="block text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            Vendor <span className="text-slate-400 normal-case font-medium tracking-normal">(optional)</span>
          </label>
        </div>
        <VendorSearch value={form.sellerId} onChange={(v) => set("sellerId", v)} />
      </div>

      {/* Hero image (optional) */}
      <div>
        <div className="mb-2">
          <label className="block text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            Hero image <span className="text-slate-400 normal-case font-medium tracking-normal">(optional)</span>
          </label>
        </div>
        <label
          className="flex items-center gap-4 px-4 py-4 rounded-2xl border border-dashed border-indigo-200 hover:border-indigo-400 bg-indigo-50/30 hover:bg-indigo-50/50 transition cursor-pointer"
          data-testid="quick-create-hero-upload"
        >
          <div className="w-12 h-12 rounded-xl bg-white text-indigo-500 border border-indigo-100 flex items-center justify-center shrink-0 overflow-hidden">
            {form.thumb ? (
              <img src={form.thumb} alt="thumb" className="w-full h-full object-cover" />
            ) : (
              <ImagePlus size={18} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[12.5px] font-semibold text-slate-900">
              {form.thumb ? "Hero image uploaded" : "Upload image"}
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {form.thumb ? "Click to replace" : "Add a photo to make your property stand out."}
            </p>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-indigo-200 text-[12px] font-semibold text-indigo-700 bg-white shrink-0">
            <Upload size={12} /> Upload image
          </span>
          <input
            type="file"
            accept="image/*"
            onChange={onThumb}
            className="sr-only"
            data-testid="property-thumb-upload"
          />
        </label>
      </div>
    </div>
  );
};

// ─── Asking-price helpers ───────────────────────────────────────────────────
//
// iter-alignment · P0 · Edit Property — Asking Price Persistence
// (2026-02-28)
// -----------------------------------------------------------------
// The Property SSOT stores `price` as a string ("725000") that
// passes the backend's `_validate_price` (accepts £-prefixed +
// comma-formatted values but the canonical form is digits-only).
// The Edit drawer shows the value live-formatted as "£700,000"
// but strips the £ / commas / spaces before persisting so the
// backend row is always digits-only and downstream `formatGBP`
// calls read cleanly.
const _priceDigits = (raw) => {
  if (raw === null || raw === undefined) return "";
  return String(raw).replace(/[^\d]/g, "");
};
const _priceDisplay = (raw) => {
  const digits = _priceDigits(raw);
  if (!digits) return "";
  const n = Number(digits);
  if (!Number.isFinite(n)) return "";
  return `£${n.toLocaleString("en-GB")}`;
};

// ─── Compact Edit body ──────────────────────────────────────────────────────
//
// iter-alignment · P0 · Property Edit Drawer — Compact Redesign
// (2026-02-28)
// -----------------------------------------------------------------
// The Edit flow used to jump the operator through the same 5-stage
// wizard as first-time add.  Once a property exists the operator is
// only ever tweaking a few fields, so this component collapses the
// entire edit UI into a single scroll-free desktop pane matching
// the approved reference:
//
//   Property details  — address · postcode · type · beds · baths
//                       · asking price · tenure · epc · parking
//                       · spaces · BSA Yes/No
//   Vendor & ownership — canonical vendor contact · Add new vendor
//                       · lead negotiator · office/branch derived
//                       from the negotiator
//   Sticky footer     — sync message · Cancel · contextual
//                       validation · Save changes
//
// Reuses the existing `PremiumInput` / `PremiumSelect` primitives,
// the `VendorSearch` component, and the same `updateProperty` +
// canonical Contacts SSOT — no new components, no new endpoint.
// -----------------------------------------------------------------
const EditPropertyBody = ({ form, set, errors, negotiators, onPickNegotiator, activeDeal }) => {
  /* iter-alignment · P0 · Edit Property — Agreed Price swap
   * (2026-02-28)
   * -----------------------------------------------------------------
   * When the property has an active transaction (a non-terminal
   * deal), the "Asking price" field is repurposed to edit the
   * deal-level `agreed_price` instead.  The property's own
   * `property.price` is left untouched so the listing / marketing
   * asking price stays canonical on genuine property surfaces.
   *
   * The write flows through the existing chainStore mutation
   * `updateDeal` with `{agreedPrice}` — the same path the Deal
   * Room hero uses — so every consumer of `deal.agreedPrice`
   * (Deal Room, Deal Rooms list, Command Centre, Pipeline,
   * Forecasting) reflects the new value in the same tick.
   *
   * Rendered value falls through: local form draft →
   * canonical `activeDeal.agreedPrice` → empty.  Digits-only
   * normalisation matches the hero editor. */
  const priceIsAgreedMode = !!activeDeal;
  const agreedRaw = form.agreedPrice ?? activeDeal?.agreedPrice ?? "";
  const showParkingSpaces = !!form.parking && !["None", "On-street"].includes(form.parking);
  const bedsOptions = ["Studio", "1", "2", "3", "4", "5", "6", "7", "8+"];
  const bathsOptions = ["1", "2", "3", "4", "5+"];
  const epcOptions = ["A", "B", "C", "D", "E", "F", "G"];
  // Custom labels on the EPC dropdown so an operator sees the
  // full "B (84)" style when the value is set, but still picks
  // from the canonical A-G vocabulary on change.
  const epcLabel = (v) => {
    if (!v) return "";
    const s = String(v).trim();
    return s;
  };

  return (
    <div className="pt-1 pb-2 space-y-8" data-testid="edit-property-body">
      {/* ── Property details ────────────────────────────────────── */}
      <section>
        <h2 className="text-[15px] font-bold text-slate-900 mb-4">Property details</h2>
        <div className="grid grid-cols-12 gap-x-4 gap-y-5">
          <div className="col-span-12 md:col-span-6">
            <PremiumInput
              label="Address"
              required
              value={form.address}
              onChange={(v) => set("address", v)}
              testid="property-address"
              error={errors.address}
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            <PremiumInput
              label="Area / locality"
              value={form.locality || ""}
              onChange={(v) => set("locality", v)}
              placeholder="e.g. Islington"
              maxLength={120}
              testid="property-locality"
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            <PremiumInput
              label="Postcode"
              required
              value={form.postcode}
              onChange={(v) => set("postcode", v)}
              testid="property-postcode"
              error={errors.postcode}
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            <PremiumSelect
              label="Property type"
              required
              value={form.type || ""}
              onChange={(v) => set("type", v)}
              placeholder="Select property type"
              options={TYPES.map((t) => ({ value: t, label: t }))}
              error={errors.type}
              testid="property-type"
            />
          </div>

          <div className="col-span-6 md:col-span-3">
            <PremiumSelect
              label="Bedrooms"
              required
              value={String(form.beds || "")}
              onChange={(v) => set("beds", v)}
              options={bedsOptions.map((b) => ({ value: b, label: b }))}
              error={errors.beds}
              testid="property-beds"
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            <PremiumSelect
              label="Bathrooms"
              required
              value={String(form.baths || "")}
              onChange={(v) => set("baths", v)}
              options={bathsOptions.map((b) => ({ value: b, label: b }))}
              error={errors.baths}
              testid="property-baths"
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            {priceIsAgreedMode ? (
              <PremiumInput
                label="Agreed price"
                required
                value={_priceDisplay(agreedRaw)}
                onChange={(v) => set("agreedPrice", _priceDigits(v))}
                placeholder="£500,000"
                hint="Updates the linked deal’s agreed price. Property asking price stays unchanged."
                testid="property-agreed-price"
                inputMode="numeric"
              />
            ) : (
              <PremiumInput
                label="Asking price"
                required
                value={_priceDisplay(form.price)}
                onChange={(v) => set("price", _priceDigits(v))}
                placeholder="£700,000"
                hint="Deal-level agreed price is separate and unchanged."
                testid="property-price"
                error={errors.price}
                inputMode="numeric"
              />
            )}
          </div>
          <div className="col-span-6 md:col-span-3">
            <PremiumSelect
              label="Tenure"
              required
              value={form.tenure || ""}
              onChange={(v) => set("tenure", v)}
              options={TENURES.map((t) => ({ value: t, label: t }))}
              error={errors.tenure}
              testid="property-tenure"
            />
          </div>

          <div className="col-span-6 md:col-span-3">
            <PremiumSelect
              label="EPC rating"
              required={false}
              value={epcLabel(form.epc)}
              onChange={(v) => set("epc", v)}
              placeholder="Select rating"
              options={epcOptions.map((r) => ({ value: r, label: r }))}
              clearable
              testid="property-epc"
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            <PremiumSelect
              label="Parking"
              value={form.parking || "None"}
              onChange={(v) => set("parking", v)}
              options={PARKING_OPTIONS.map((p) => ({ value: p, label: p }))}
              testid="property-parking"
            />
          </div>
          {showParkingSpaces && (
            <div className="col-span-6 md:col-span-3">
              <PremiumInput
                label="Parking spaces"
                type="number"
                value={String(form.parkingSpaces ?? "")}
                onChange={(v) => set("parkingSpaces", v)}
                placeholder="0"
                testid="property-parking-spaces"
              />
            </div>
          )}
          {/* Building Safety Act Yes / No / Not confirmed (blank) + optional EWS1 rating, kept side by side */}
          <div className="col-span-12 md:col-span-6 grid grid-cols-12 gap-x-4 gap-y-5" data-testid="property-building-safety-row">
          <div className="col-span-12 sm:col-span-7">
            <label className="block text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">
              Building Safety Act applies
            </label>
            <div className="inline-flex rounded-xl border border-slate-200 bg-white h-11 p-1 gap-1 w-full" role="radiogroup" aria-label="Building Safety Act applies">
              <button
                type="button"
                onClick={() => set("bsaApplies", true)}
                aria-pressed={form.bsaApplies === true}
                className={`flex-1 rounded-lg text-[13px] font-semibold transition ${
                  form.bsaApplies === true
                    ? "bg-slate-900 text-white shadow-sm"
                    : "bg-transparent text-slate-600 hover:text-slate-900"
                }`}
                data-testid="property-bsa-yes"
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => set("bsaApplies", false)}
                aria-pressed={form.bsaApplies === false}
                className={`flex-1 rounded-lg text-[13px] font-semibold transition ${
                  form.bsaApplies === false
                    ? "bg-slate-900 text-white shadow-sm"
                    : "bg-transparent text-slate-600 hover:text-slate-900"
                }`}
                data-testid="property-bsa-no"
              >
                No
              </button>
              <button
                type="button"
                onClick={() => set("bsaApplies", null)}
                aria-pressed={form.bsaApplies == null}
                className={`flex-[1.6] rounded-lg text-[12.5px] font-semibold whitespace-nowrap transition ${
                  form.bsaApplies == null
                    ? "bg-slate-900 text-white shadow-sm"
                    : "bg-transparent text-slate-600 hover:text-slate-900"
                }`}
                data-testid="property-bsa-unconfirmed"
              >
                Not confirmed
              </button>
            </div>
          </div>
          <div className="col-span-6 sm:col-span-5">
            <PremiumSelect
              label="EWS1 rating"
              value={form.ews1Rating || ""}
              onChange={(v) => set("ews1Rating", v || null)}
              placeholder="Not recorded"
              options={EWS1_RATINGS.map((r) => ({ value: r, label: r }))}
              clearable
              testid="property-ews1-rating"
            />
          </div>
          </div>
        </div>
      </section>

      <div className="h-px bg-slate-200/70" aria-hidden="true" />

      {/* ── Vendor & responsibility ─────────────────────────────── */}
      <section>
        <h2 className="text-[15px] font-bold text-slate-900 mb-4">Vendor &amp; responsibility</h2>
        <div className="grid grid-cols-12 gap-x-4 gap-y-5">
          <div className="col-span-12 md:col-span-5">
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Vendor / seller<span className="text-rose-500 ml-0.5" aria-hidden="true">*</span>
              </label>
              <button
                type="button"
                onClick={() => openAddContact({ role: "Seller" })}
                className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-violet-700 hover:text-violet-900"
                data-testid="edit-property-add-vendor"
              >
                <User size={11} /> + Add new vendor
              </button>
            </div>
            <VendorSearch value={form.sellerId} onChange={(v) => set("sellerId", v)} />
            {errors.sellerId ? (
              <p className="mt-1.5 text-[11px] text-rose-600 leading-snug" role="alert">{errors.sellerId}</p>
            ) : null}
          </div>
          <div className="col-span-6 md:col-span-4">
            <PremiumSelect
              label="Lead negotiator"
              required
              value={form.agentId || ""}
              onChange={onPickNegotiator}
              placeholder="Unassigned"
              clearable
              searchable={negotiators.length > 6}
              options={negotiators}
              testid="property-agent"
              error={errors.agentId}
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            <PremiumInput
              label="Office / Branch"
              value={form.office || ""}
              onChange={(v) => set("office", v)}
              placeholder="e.g. Mayfair"
              testid="property-office"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

// ─── Main drawer component ───────────────────────────────────────────────────
export const AddPropertyDrawer = () => {
  const { addProperty, updateProperty, contacts, properties, deals, updateDeal } = useChainStore();
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState({});
  const [form, setForm] = useState(initialForm());
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [savedProp, setSavedProp] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [draftJustSaved, setDraftJustSaved] = useState(false);
  const isEditing = Boolean(preset?._editing && preset?.id);
  /* iter-alignment · P0 · Edit Property — Agreed Price swap
   * (2026-02-28)
   * -----------------------------------------------------------------
   * The Edit Property drawer swaps the "Asking price" input for
   * "Agreed price" whenever the property has an active
   * transaction (any non-terminal deal — stages `completed`,
   * `fallen_through` and `banked` are terminal).  The write goes
   * to `deal.agreed_price` via the existing `updateDeal(dealId,
   * {agreedPrice})` mutation; `property.price` is never touched
   * so listing / marketing surfaces keep the canonical asking
   * price.  We compute the match once here and thread the deal
   * ref into `EditPropertyBody` and `submit()` — no new endpoint,
   * no duplicate state. */
  const TERMINAL_DEAL_STAGES = ["completed", "fallen_through", "banked"];
  const activeDeal = useMemo(() => {
    if (!isEditing || !preset?.id) return null;
    const arr = Array.isArray(deals) ? deals : [];
    const match = arr.filter((d) => (d.propertyId || d.property_id) === preset.id);
    if (match.length === 0) return null;
    const live = match.find((d) => !TERMINAL_DEAL_STAGES.includes(String(d.stage || "").toLowerCase()));
    return live || null;
  }, [deals, isEditing, preset?.id]);
  const draftKey = useRef(`chainflow:propertyDraft`).current;
  const draftMetaKey = useRef(`chainflow:propertyDraft:meta`).current;

  useEffect(() => {
    return cfBus.on("cf:open-property", (p) => {
      setPreset(p || {});
      // Restore draft if not editing and no preset id passed
      if (!p?._editing) {
        try {
          const draft = JSON.parse(localStorage.getItem(draftKey) || "null");
          setForm(initialForm(draft || p));
          const meta = JSON.parse(localStorage.getItem(draftMetaKey) || "null");
          if (meta?.savedAt) setLastSavedAt(meta.savedAt);
        } catch (_e) { setForm(initialForm(p)); }
      } else {
        setForm(initialForm(p));
        setLastSavedAt(null);
      }
      setErrors({});
      setSavedProp(null);
      setStep(1);
      setSubmitting(false);
      setDraftJustSaved(false);
      setOpen(true);
    });
  }, [draftKey, draftMetaKey]);

  // Auto-save with 1.2s debounce — only when drawer is open, not editing, and form has any meaningful content.
  useEffect(() => {
    if (!open || isEditing || savedProp) return undefined;
    const hasContent = !!(form.address?.trim() || form.postcode?.trim() || form.type || form.price?.trim());
    if (!hasContent) return undefined;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify(form));
        const ts = Date.now();
        localStorage.setItem(draftMetaKey, JSON.stringify({ savedAt: ts }));
        setLastSavedAt(ts);
      } catch (_e) { /* quota / private mode — silent */ }
    }, 1200);
    return () => clearTimeout(t);
  }, [form, open, isEditing, savedProp, draftKey, draftMetaKey]);

  // Tick the "X mins ago" label every 30s while drawer is open
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [open]);
  const lastSavedLabel = useMemo(() => {
    if (!lastSavedAt) return null;
    const diff = nowTick - lastSavedAt;
    if (diff < 30_000) return "Saved just now";
    const m = Math.floor(diff / 60_000);
    if (m < 60) return `Last saved ${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `Last saved ${h}h ago`;
    return `Last saved ${Math.floor(h / 24)}d ago`;
  }, [lastSavedAt, nowTick]);

  const close = () => {
    setOpen(false);
    setSavedProp(null);
    setSubmitting(false);
  };

  const { options: negotiatorOptions, pick: resolveNegotiator } = useLeadNegotiators({
    active: open, currentAgentId: form.agentId, office: form.office,
  });
  // Office / branch is independent; a negotiator only fills a blank office.
  const pickNegotiator = async (v) => {
    try {
      const { contactId, officeName } = await resolveNegotiator(v);
      set("agentId", contactId);
      if (contactId && !String(form.office || "").trim() && officeName) set("office", officeName);
    } catch (e) {
      toast.error("Couldn't assign that negotiator", { description: apiError(e) });
    }
  };
  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    // Clear that field's error live
    if (errors[k]) setErrors((e) => { const n = { ...e }; delete n[k]; return n; });
    return next;
  });

  const onThumb = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // iter-370 — Compress-on-upload.
    //
    // Real phone photos are 3-8 MB and base64 out to 4-11 MB. Anything
    // over ~900 KB is rejected by the API (below the k8s ingress
    // 1 MiB cap so Cloudflare never sees a body-size origin error).
    // Downscale + JPEG-recompress on the client so a 6 MB HEIC still
    // slides in at ~250 KB with the naked eye unable to spot the drop.
    const MAX_DIM = 1600;        // longest edge — hero cards render well below this
    const TARGET_BYTES = 700_000; // hard target, well below the API's 900 KB cap
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (ev) => {
      const raw = ev.target.result;
      img.onload = () => {
        const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        // Try progressively lower quality until we fit under the target.
        let quality = 0.86;
        let out = canvas.toDataURL("image/jpeg", quality);
        while (out.length > TARGET_BYTES && quality > 0.35) {
          quality -= 0.12;
          out = canvas.toDataURL("image/jpeg", quality);
        }
        set("thumb", out);
      };
      img.onerror = () => {
        // Non-image (SVG / HEIC that can't decode) — fall back to raw
        // data URL. Server-side cap will reject cleanly with a toast
        // if it's still too large.
        set("thumb", raw);
      };
      img.src = raw;
    };
    reader.readAsDataURL(file);
  };

  const score = useMemo(() => computeScore(form), [form]);
  const stepErrors = useMemo(() => stageValidator[step] ? stageValidator[step](form) : {}, [step, form]);
  const canContinue = Object.keys(stepErrors).length === 0;

  const goNext = () => {
    if (!canContinue) {
      setErrors(stepErrors);
      toast.error("Please complete the required fields", { description: Object.values(stepErrors).join(" · ") });
      return;
    }
    setErrors({});
    if (step < 5) setStep(step + 1);
  };
  const goBack = () => { setErrors({}); if (step > 1) setStep(step - 1); };
  const jumpTo = (s) => { setErrors({}); setStep(s); };

  const saveDraft = () => {
    try {
      localStorage.setItem(draftKey, JSON.stringify(form));
      const ts = Date.now();
      localStorage.setItem(draftMetaKey, JSON.stringify({ savedAt: ts }));
      setLastSavedAt(ts);
      setDraftJustSaved(true);
      setTimeout(() => setDraftJustSaved(false), 1800);
      toast.success("Draft saved", { description: "You can resume this property later." });
    } catch (_e) {
      toast.error("Could not save draft", { description: "Storage may be unavailable." });
    }
  };

  // Only send the BSA flag when it changed, so unrelated saves don't log BSA audit rows.
  const withBsaIfChanged = (patch) => {
    const next = { ...patch };
    if ((patch.bsaApplies ?? null) === (preset?.bsaApplies ?? null)) delete next.bsaApplies;
    else next.bsaApplies = patch.bsaApplies ?? null;
    // Parking spaces is hidden for None / On-street, so don't keep a stale count.
    if (!next.parking || ["None", "On-street"].includes(next.parking)) next.parkingSpaces = null;
    return next;
  };

  const submit = (opts = {}) => {
    // iter-273 — `startTransaction: true` calls openCreateDeal()
    // immediately after the property saves so the operator flows
    // straight into the Deal Room setup.
    const startTransaction = !!opts.startTransaction;
    // Run all validators
    //
    // iter-alignment · P0 · Edit Drawer Save Unblock (2026-02-28)
    // ---------------------------------------------------------------
    // When editing a legacy row where the pilot seed left fields
    // like `type` or `sellerId` empty, the required guard blocks
    // partial updates (Asking price / BSA / Parking).  We drop
    // required-errors on any field that was ALREADY empty in the
    // preset — mirroring the `editErrors` gate above so the button
    // and the submit path stay in lockstep.  Fields the operator
    // actively cleared (was non-empty in preset → empty now) still
    // fail validation as before.
    const rawEditErrors = { ...stage1Errors(form), ...stage2Errors(form), ...stage3Errors(form) };
    const wasEmpty = (v) => v === undefined || v === null || v === "" ||
      (typeof v === "string" && !v.trim());
    const editPresetMap = {
      address:  preset?.address,
      postcode: preset?.postcode,
      type:     preset?.type,
      beds:     preset?.beds,
      baths:    preset?.baths,
      price:    preset?.price,
      tenure:   preset?.tenure,
      sellerId: preset?.sellerId,
    };
    if (isEditing) {
      for (const [field, presetVal] of Object.entries(editPresetMap)) {
        if (rawEditErrors[field] && wasEmpty(presetVal)) delete rawEditErrors[field];
      }
    }
    const all = isEditing ? rawEditErrors : quickCreateErrors(form);
    if (Object.keys(all).length) {
      setErrors(all);
      // Legacy edit wizard — jump to first failing step.
      if (isEditing) {
        const s1 = stage1Errors(form);
        const s2 = stage2Errors(form);
        const firstStep = Object.keys(s1).length ? 1 : Object.keys(s2).length ? 2 : 3;
        setStep(firstStep);
      }
      toast.error("Some required details are missing", { description: Object.values(all).join(" · ") });
      return;
    }
    setSubmitting(true);
    setTimeout(async () => {
      try {
        let p;
        if (isEditing) {
          /* iter-alignment · P0 · Edit Property — Agreed Price
           * swap (2026-02-28)
           * -----------------------------------------------------
           * When the drawer is showing "Agreed price" (active
           * deal exists), the operator's edit is routed to
           * `updateDeal(dealId, {agreedPrice})` — the same path
           * the Deal Room hero uses.  `property.price` MUST NOT
           * change, so we strip `price` from the property patch
           * and hard-restore the preset value locally so any
           * downstream re-render sees the unchanged asking
           * price. */
          if (activeDeal) {
            const nextDigits = _priceDigits(form.agreedPrice ?? activeDeal.agreedPrice ?? "");
            const currDigits = _priceDigits(activeDeal.agreedPrice ?? "");
            if (nextDigits && nextDigits !== currDigits) {
              try {
                await updateDeal(activeDeal.id, { agreedPrice: nextDigits });
              } catch (e) {
                setSubmitting(false);
                const msg = apiError(e, "Couldn't update agreed price");
                toast.error("Agreed price not saved", { description: msg });
                return;
              }
            }
            const propertyPatch = { ...form, price: preset?.price ?? "" };
            delete propertyPatch.agreedPrice;
            p = await updateProperty(preset.id, withBsaIfChanged(propertyPatch));
          } else {
            p = await updateProperty(preset.id, withBsaIfChanged(form));
          }
          toast.success(`${(p.address || "").split(",")[0]} updated`);
          preset.onSaved?.(p);
          setSubmitting(false);
          close();
          return;
        }
        p = await addProperty(form);
        try {
          localStorage.removeItem(draftKey);
          localStorage.removeItem(draftMetaKey);
        } catch (_e) { /* noop */ }
        setLastSavedAt(null);
        setSubmitting(false);
        toast.success(`${(p.address || "").split(",")[0]} saved`, { description: `${p.type || ""} · ${p.price || ""}` });
        preset.onSaved?.(p);
        if (startTransaction) {
          // Close this drawer and immediately open Create Deal Room
          // with the new property preselected. Same chainStore bus
          // event used everywhere else, so analytics / audit hooks
          // continue to fire.
          close();
          // iter-274 — Hand the freshly-saved property OFF cleanly:
          //   • property, vendor, negotiator are pre-filled on the
          //     Deal Room wizard's preset
          //   • `stage: "offer"` is the canonical earliest baseline
          //     (Property Created / Pre-Market). The operator can
          //     change it later from the stage rail. The legacy
          //     stage map bridges `instruction → offer` so older
          //     fixtures still flow through cleanly.
          openCreateDeal({
            propertyId: p.id,
            sellerId:   p.sellerId,
            agentId:    p.agentId,
            agreedPrice: p.price,
            tenure:     p.tenure ? String(p.tenure).toLowerCase() : undefined,
            stage:      "offer",
            // Signals the wizard that everything captured on the
            // Create Property screen is ready — the wizard skips
            // the participants step so the operator never re-enters
            // data they just typed.
            fromQuickCreate: true,
          });
        } else if (preset.onSaved) {
          // Opened from the new-deal wizard: hand straight back to it.
          close();
        } else {
          setSavedProp(p);
        }
      } catch (err) {
        setSubmitting(false);
        const msg = apiError(err);
        const code = apiErrorCode(err);
        const field = apiErrorField(err);

        // Inline-highlight the offending input + jump the wizard to the
        // step that owns it, so the operator sees exactly what to fix.
        if (field && typeof field === "string") {
          setErrors((prev) => ({ ...prev, [field]: msg }));
          // Map field → wizard step so the operator isn't stranded.
          const STEP_BY_FIELD = {
            address: 1, postcode: 1, type: 1, beds: 1, baths: 1,
            price: 2, rent: 2, tenure: 2, epc: 2, svc: 2, status: 2,
            seller_id: 3, agent_id: 3, office: 3, notes: 3,
          };
          const targetStep = STEP_BY_FIELD[field];
          if (targetStep) setStep(targetStep);
          requestAnimationFrame(() => {
            const el = document.querySelector(`[name="${field}"], [data-testid="property-${field.replace(/_/g, "-")}"]`);
            if (el && typeof el.focus === "function") el.focus();
          });
        }

        let title;
        if (code === "duplicate_property") title = "Address already in use";
        else if (code === "contact_not_in_org") title = "Contact not in workspace";
        else if (code === "workspace_missing") title = "Workspace not set";
        else if (code === "property_create_failed" || code === "property_update_failed") title = "Save failed";
        else if (err?.response?.status === 422) title = "Some fields need attention";
        else if (err?.response?.status === 401) title = "Session expired";
        else if (err?.response?.status === 403) title = "Not permitted";
        else title = isEditing ? "Could not save changes" : "Could not save property";

        toast.error(title, { description: msg });
      }
    }, 700);
  };

  // ─── Stage renderers ───────────────────────────────────────────────────────
  const renderStage1 = () => {
    const showLease = form.tenure === "Leasehold" || form.tenure === "Share of Freehold";
    return (
    <StagePane step={1}>
      <SectionHeader icon={Building2} label="Property details" hint="Address, type and price the listing will go to market with." />
      <div className="grid grid-cols-12 gap-x-4 gap-y-5">
        {/* Live preview card */}
        <div className="col-span-12 flex gap-3 p-3 rounded-2xl bg-gradient-to-r from-indigo-50/80 via-indigo-50/40 to-transparent border border-indigo-100">
          <label className="relative w-24 h-24 rounded-xl overflow-hidden border-2 border-dashed border-indigo-300 bg-white flex items-center justify-center cursor-pointer hover:border-indigo-500 transition-colors shrink-0" data-testid="property-thumb-upload">
            {form.thumb ? <img src={form.thumb} alt="thumb" className="w-full h-full object-cover" /> : <ImagePlus size={20} className="text-indigo-500" />}
            <input type="file" accept="image/*" onChange={onThumb} className="absolute inset-0 opacity-0 cursor-pointer" />
          </label>
          <div className="flex-1 min-w-0 self-center">
            <p className="text-[10.5px] uppercase tracking-[0.18em] text-indigo-700 font-semibold">Live preview</p>
            <p className="text-[14px] font-bold text-slate-900 mt-0.5 truncate">{form.address || "Property address"}</p>
            <p className="text-[11.5px] text-slate-500 truncate">
              {[form.postcode, form.type, form.tenure].filter(Boolean).join(" · ") || "Postcode · Type · Tenure"}
            </p>
            {form.price && (
              <p className="text-[14px] font-bold text-indigo-700 cf-num mt-1">{form.price}</p>
            )}
          </div>
        </div>

        <div className="col-span-12">
          <PremiumInput label="Address" required value={form.address} onChange={(v) => set("address", v)} placeholder="12 Mersey Reach, Aigburth, Liverpool" error={errors.address} autoFocus testid="property-address" />
        </div>

        <div className="col-span-12 md:col-span-4">
          <PremiumInput
            label="Postcode"
            required
            value={form.postcode}
            onChange={(v) => set("postcode", String(v).toUpperCase())}
            placeholder="L17 6AS"
            maxLength={9}
            autoComplete="postal-code"
            error={errors.postcode}
            testid="property-postcode"
          />
        </div>
        <div className="col-span-12 md:col-span-4">
          <PremiumInput label="Area / locality" value={form.locality || ""} onChange={(v) => set("locality", v)} placeholder="e.g. Islington" maxLength={120} testid="property-locality" />
        </div>
        <div className="col-span-12 md:col-span-4">
          <PremiumSelect label="Property type" value={form.type} onChange={(v) => set("type", v)} placeholder="Select type…" error={errors.type} required testid="property-type"
            options={TYPES.map((t) => ({ value: t, label: t }))} />
        </div>

        <div className="col-span-12 md:col-span-3">
          <PremiumInput label="Bedrooms" type="number" inputMode="numeric" value={form.beds} onChange={(v) => set("beds", v)} error={errors.beds} required testid="property-beds" />
        </div>
        <div className="col-span-12 md:col-span-3">
          <PremiumInput label="Bathrooms" type="number" inputMode="numeric" value={form.baths} onChange={(v) => set("baths", v)} error={errors.baths} required testid="property-baths" />
        </div>
        <div className="col-span-12 md:col-span-6">
          <PremiumInput
            label="Asking price"
            format="currency"
            value={form.price}
            onChange={(v) => set("price", v)}
            placeholder="725,000"
            autoComplete="off"
            error={errors.price}
            required
            testid="property-price"
          />
        </div>

        <div className="col-span-12 md:col-span-6">
          <PremiumSelect label="Tenure" value={form.tenure} onChange={(v) => set("tenure", v)} placeholder="Select tenure…" error={errors.tenure} required testid="property-tenure"
            options={TENURES.map((s) => ({ value: s, label: s }))} />
        </div>

        {showLease && (
          <>
            <div className="col-span-12 md:col-span-4">
              <PremiumInput label="Lease length remaining" hint="Years left on the lease" value={form.leaseYears} onChange={(v) => set("leaseYears", v)} placeholder="e.g. 125 years" testid="property-lease-years" />
            </div>
            <div className="col-span-12 md:col-span-4">
              <PremiumInput label="Ground rent" format="currency" value={form.rent} onChange={(v) => set("rent", v)} placeholder="250 / yr" testid="property-rent" />
            </div>
            <div className="col-span-12 md:col-span-4">
              <PremiumInput label="Service charge" format="currency" value={form.svc} onChange={(v) => set("svc", v)} placeholder="2,100 / yr" testid="property-svc" />
            </div>
          </>
        )}

        {/* iter-259 — Lightweight operational parking metadata.
            Dropdown + optional spaces count. NOT a marketing block —
            just enough scan-value for the operator. */}
        <div className="col-span-12 md:col-span-8">
          <PremiumSelect
            label="Parking"
            value={form.parking || "None"}
            onChange={(v) => set("parking", v)}
            placeholder="Parking type"
            testid="property-parking"
            options={PARKING_OPTIONS.map((p) => ({ value: p, label: p }))}
          />
        </div>
        <div className="col-span-12 md:col-span-4">
          <PremiumInput
            label="Spaces"
            type="number"
            inputMode="numeric"
            value={form.parkingSpaces}
            onChange={(v) => set("parkingSpaces", v)}
            placeholder="0"
            hint={form.parking === "None" || !form.parking ? "Optional" : "e.g. 1"}
            testid="property-parking-spaces"
          />
        </div>
      </div>
    </StagePane>
    );
  };

  const renderStage2 = () => {
    return (
      <StagePane step={2}>
        <SectionHeader icon={User} label="Vendor information" hint="Who owns this property and which negotiator is leading the listing." />
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12">
            <label className="block text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">
              Vendor / Seller<span className="text-rose-500 ml-0.5" aria-hidden="true">*</span>
            </label>
            <VendorSearch value={form.sellerId} onChange={(v) => set("sellerId", v)} />
            {errors.sellerId ? (
              <p className="mt-1.5 text-[11px] text-rose-600 leading-snug" role="alert">{errors.sellerId}</p>
            ) : (
              <p className="mt-1.5 text-[11px] text-slate-500 leading-snug">The legal owner the office is acting for.</p>
            )}
          </div>
          <div className="col-span-12 md:col-span-6">
            <PremiumSelect label="Lead negotiator" value={form.agentId || ""} onChange={pickNegotiator} placeholder="Unassigned" clearable searchable={negotiatorOptions.length > 6} testid="property-agent"
              options={negotiatorOptions} />
          </div>
          <div className="col-span-12 md:col-span-6">
            <PremiumInput label="Office / Branch" value={form.office} onChange={(v) => set("office", v)} testid="property-office" />
          </div>
        </div>
      </StagePane>
    );
  };

  const renderStage3 = () => {
    const isLeasehold = form.tenure === "Leasehold" || form.tenure === "Share of Freehold";
    return (
      <StagePane step={3}>
        <SectionHeader icon={ShieldCheck} label="Compliance" hint="EPC, AML and any solicitor-pack notes." />
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6">
            <PremiumInput
              label="EPC rating"
              value={form.epc}
              onChange={(v) => set("epc", v)}
              placeholder="B (84)"
              hint={!form.epc ? "Reminder: request from vendor or order survey." : undefined}
              testid="property-epc"
            />
          </div>
          <div className="col-span-12 md:col-span-6">
            <PremiumInput
              label="Tenure (set on Step 1)"
              value={form.tenure || "—"}
              onChange={() => {}}
              readOnly
              hint={`Currently: ${form.tenure || "—"}`}
              testid="property-tenure-echo"
            />
          </div>
          {isLeasehold && (
            <div className="col-span-12 rounded-xl border border-amber-200 bg-amber-50/60 p-3.5 cf-fade-up flex items-start gap-2.5" data-testid="leasehold-prompt">
              <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" />
              <div>
                <p className="text-[12.5px] font-semibold text-amber-900">Leasehold flagged</p>
                <p className="text-[11.5px] text-amber-800/80 leading-snug mt-0.5">Start AML / lease pack now — buyers will request the LPE1 and LPE2 forms early.</p>
              </div>
            </div>
          )}
          <div className="col-span-12">
            <PremiumTextarea
              label="AML / Compliance notes"
              value={form.aml}
              onChange={(v) => set("aml", v)}
              placeholder={isLeasehold ? "LPE1/LPE2 status, lease length, ground rent review schedule…" : "Source of funds noted, ID verified, sanctions check complete…"}
              rows={3}
              autoResize
              maxRows={6}
              testid="property-aml"
            />
          </div>
        </div>
      </StagePane>
    );
  };

  const renderStage4 = () => (
    <StagePane step={4}>
      <SectionHeader icon={Megaphone} label="Marketing setup" hint="Listing copy, photography and viewing windows." />
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-6">
          <PremiumSelect label="Listing status" value={form.status} onChange={(v) => set("status", v)} testid="property-status"
            options={STATUSES.map((s) => ({ value: s, label: s }))} />
        </div>
        <div className="col-span-12 md:col-span-6">
          <label className="block text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">Photography booked</label>
          <button
            type="button"
            onClick={() => set("photographyBooked", !form.photographyBooked)}
            className={`h-10 px-4 rounded-xl text-[12.5px] font-semibold transition-all border w-full inline-flex items-center justify-between ${form.photographyBooked ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
            data-testid="property-photography"
          >
            {form.photographyBooked ? "Scheduled" : "Not yet booked"}
            {form.photographyBooked ? <CheckCircle2 size={14} /> : <Calendar size={13} />}
          </button>
        </div>
        <div className="col-span-12">
          <PremiumTextarea label="Listing description" hint="2 short paragraphs — buyers, agents and portals will see this first." value={form.description} onChange={(v) => set("description", v)} placeholder="A quietly elegant five-bed in Aigburth's leafiest pocket, beautifully extended into the rear garden…" rows={4} autoResize maxRows={8} testid="property-description" />
        </div>
        <div className="col-span-12">
          <PremiumInput label="Viewing availability" value={form.viewingAvailability} onChange={(v) => set("viewingAvailability", v)} placeholder="Tuesdays & Thursdays after 4pm, Saturday mornings" testid="property-viewings" />
        </div>
        <div className="col-span-12">
          <PremiumTextarea label="Internal notes" value={form.notes} onChange={(v) => set("notes", v)} placeholder="Compliance flags, vendor preferences, photography brief…" rows={2} autoResize maxRows={5} testid="property-notes" />
        </div>
      </div>
    </StagePane>
  );

  const renderStage5 = () => (
    <StagePane step={5}>
      <SectionHeader icon={Rocket} label="Launch review" hint="Final once-over — edit any field inline before going live." />
      <ReviewSummary form={form} contacts={contacts} score={score} onJump={jumpTo} />
    </StagePane>
  );

  const stageRenderers = [null, renderStage1, renderStage2, renderStage3, renderStage4, renderStage5];

  // ─── Footer (sticky) ───────────────────────────────────────────────────────
  const isLastStep = step === 5;
  const primaryLabel = isEditing ? "Save changes" : isLastStep ? "Save & Launch" : "Save & Continue";
  const primaryDisabled = !canContinue || submitting;

  // iter-273 — Single-screen footer: dual-CTA pattern.
  // Primary: "Create & Start Transaction" — saves, then opens
  // Create Deal Room. Secondary: "Create Property" — saves only.
  // Save Draft + last-saved indicator preserved.
  const quickFooter = (
    <div className="flex w-full items-center gap-2 flex-wrap" data-testid="quick-create-footer">
      <button onClick={close} className="btn btn-ghost" data-testid="property-cancel">Cancel</button>
      <button
        onClick={saveDraft}
        className={`btn btn-ghost transition-all ${draftJustSaved ? "!text-emerald-700" : ""}`}
        data-testid="property-save-draft"
        aria-live="polite"
      >
        {draftJustSaved ? (
          <><CheckCircle2 size={13} className="cf-fade-up" /> Draft saved</>
        ) : (
          <>Save draft</>
        )}
      </button>
      <span className="ml-auto inline-flex items-center gap-2.5 flex-wrap justify-end">
        <button
          type="button"
          onClick={() => submit({ startTransaction: false })}
          disabled={submitting}
          className="h-11 px-5 rounded-xl border border-slate-300 bg-white text-slate-900 text-[13px] font-semibold hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-slate-300/60"
          data-testid="quick-create-property-btn"
          aria-label="Create Property"
        >
          {submitting ? (
            <span className="inline-flex items-center gap-1.5"><Loader2 size={14} className="animate-spin" /> Saving…</span>
          ) : "Create Property"}
        </button>
        <button
          type="button"
          onClick={() => submit({ startTransaction: true })}
          disabled={submitting}
          className="h-11 px-5 rounded-xl bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-700 hover:to-violet-600 text-white text-[13px] font-semibold shadow-[0_8px_22px_-8px_rgba(124,58,237,0.55)] transition disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-violet-300/60"
          data-testid="quick-create-start-transaction-btn"
          aria-label="Create & Start Transaction"
        >
          {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
          Create & Start Transaction
          <ArrowRight size={14} strokeWidth={2.4} />
        </button>
      </span>
    </div>
  );

  // iter-273 — Use the single-screen experience for non-editing
  // create flows. Edit mode and the post-save "All set" screen
  // continue to use the legacy 5-stage wizard / NextActionCards
  // so existing edit / save-then-next-action behaviour is
  // preserved untouched.
  const useQuickCreate = !isEditing && !savedProp;

  // iter-alignment · P0 · Property Edit Drawer — Compact Redesign
  // (2026-02-28) — Edit mode runs the full property + vendor
  // validator up front (there's no step machine), and reports the
  // first missing field in the footer so the operator sees exactly
  // what's holding Save Changes back.
  //
  // iter-alignment · P0 · Edit Drawer Save Unblock (2026-02-28)
  // ---------------------------------------------------------------
  // The Edit drawer is a partial-update surface — an operator opens
  // it to tweak Asking price, BSA, parking, etc.  The full validator
  // suite (stage1Errors + stage2Errors) previously blocked Save
  // whenever ANY required field was empty in the loaded property,
  // which is the pilot reality (seed leaves `type`, `postcode`,
  // `sellerId` unset on legacy rows).  Result: the operator changes
  // Asking price, clicks Save, and the button is disabled with
  // "Select a property type to save" — the price update never
  // reaches the backend.
  //
  // The fix skips the required guard on any field that was ALREADY
  // empty in the preset (no regression: guard still fires the
  // moment the operator clears a previously-filled value).  Backend
  // org-isolation + partial PATCH semantics are unchanged.
  const editErrors = useMemo(() => {
    if (!isEditing) return {};
    const all = { ...stage1Errors(form), ...stage2Errors(form) };
    const wasEmpty = (v) => v === undefined || v === null || v === "" ||
      (typeof v === "string" && !v.trim());
    // Field → preset value it maps to.
    const presetMap = {
      address:  preset?.address,
      postcode: preset?.postcode,
      type:     preset?.type,
      beds:     preset?.beds,
      baths:    preset?.baths,
      price:    preset?.price,
      tenure:   preset?.tenure,
      sellerId: preset?.sellerId,
    };
    for (const [field, presetVal] of Object.entries(presetMap)) {
      if (all[field] && wasEmpty(presetVal)) delete all[field];
    }
    return all;
  }, [isEditing, form, preset]);
  const canSaveEdit = Object.keys(editErrors).length === 0;
  const editValidationHint = useMemo(() => {
    if (canSaveEdit) return null;
    if (editErrors.type)      return "Select a property type to save";
    if (editErrors.address)   return "Address is required";
    if (editErrors.postcode)  return "Postcode is required";
    if (editErrors.beds)      return "Bedrooms is required";
    if (editErrors.baths)     return "Bathrooms is required";
    if (editErrors.price)     return "Asking price is required";
    if (editErrors.tenure)    return "Tenure is required";
    if (editErrors.sellerId)  return "Select or add a vendor to save";
    return "Complete required fields to save";
  }, [canSaveEdit, editErrors]);

  const editFooter = (
    <div className="flex w-full items-center gap-3 flex-wrap" data-testid="edit-property-footer">
      <button onClick={close} className="btn btn-secondary" data-testid="property-cancel">Cancel</button>
      <span className="text-[11.5px] text-slate-500 inline-flex items-center gap-1.5" data-testid="edit-property-sync-msg">
        <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
        Shared details stay in sync
      </span>
      <div className="ml-auto flex items-center gap-3">
        {!canSaveEdit && (
          <span className="text-[11.5px] text-slate-500 inline-flex items-center gap-1.5" data-testid="edit-property-validation">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
            {editValidationHint}
          </span>
        )}
        <button
          onClick={submit}
          disabled={!canSaveEdit || submitting}
          className={`btn btn-primary !h-11 !px-5 !text-[13.5px] ${(!canSaveEdit || submitting) ? "opacity-50 cursor-not-allowed !shadow-none" : ""}`}
          data-testid="property-save"
        >
          {submitting ? (
            <><Loader2 size={14} className="animate-spin" /> Saving…</>
          ) : (
            <><CheckCircle2 size={14} /> Save changes</>
          )}
        </button>
      </div>
    </div>
  );

  const footer = savedProp ? (
    <div className="flex w-full items-center justify-end gap-2">
      <button onClick={close} className="btn btn-ghost" data-testid="property-done">Close</button>
    </div>
  ) : isEditing ? (
    editFooter
  ) : useQuickCreate ? (
    quickFooter
  ) : (
    <div className="flex w-full items-center gap-2 flex-wrap">
      {/* Left cluster */}
      <button onClick={close} className="btn btn-secondary" data-testid="property-cancel">Cancel</button>
      {!isEditing && (
        <button
          onClick={saveDraft}
          className={`btn btn-ghost transition-all ${draftJustSaved ? "!text-emerald-700" : ""}`}
          data-testid="property-save-draft"
          aria-live="polite"
        >
          {draftJustSaved ? (
            <><CheckCircle2 size={13} className="cf-fade-up" /> Draft saved</>
          ) : (
            <>Save draft</>
          )}
        </button>
      )}
      {!isEditing && lastSavedLabel && (
        <span className="text-[10.5px] text-slate-400 mx-1 inline-flex items-center gap-1.5" data-testid="property-draft-status">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 cf-pulse" />
          {lastSavedLabel}
        </span>
      )}
      {/* Center status */}
      {!canContinue && (
        <span className="text-[11.5px] text-slate-500 mx-2 hidden md:inline-flex items-center gap-1.5" data-testid="property-helper">
          <AlertTriangle size={11} className="text-amber-500" /> Complete required fields to continue
        </span>
      )}
      {/* Right cluster */}
      <div className="ml-auto flex items-center gap-2">
        {step > 1 && (
          <button onClick={goBack} className="btn btn-secondary" data-testid="property-back">
            <ArrowLeft size={13} /> Back
          </button>
        )}
        {!isLastStep ? (
          <button
            onClick={goNext}
            disabled={primaryDisabled}
            className={`btn btn-primary !h-11 !px-5 !text-[13.5px] ${primaryDisabled ? "opacity-50 cursor-not-allowed !shadow-none" : ""}`}
            data-testid="property-continue"
          >
            {primaryLabel} <ArrowRight size={14} />
          </button>
        ) : (
          <button
            onClick={submit}
            disabled={primaryDisabled}
            className={`btn btn-primary !h-11 !px-5 !text-[13.5px] ${primaryDisabled ? "opacity-50 cursor-not-allowed !shadow-none" : ""}`}
            data-testid="property-save"
          >
            {submitting ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><CheckCircle2 size={14} /> {primaryLabel}</>}
          </button>
        )}
      </div>
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  const headerExtra = !savedProp && !isEditing && !useQuickCreate ? <StageDots current={step} total={5} /> : null;
  const stageMeta = STAGES[step - 1];

  const isLeasehold = form.tenure === "Leasehold" || form.tenure === "Share of Freehold";

  return (
    <Drawer
      open={open}
      onClose={close}
      title={savedProp ? "All set" : isEditing ? "Edit property" : useQuickCreate ? "Create Property" : (stageMeta?.label || "Add property")}
      eyebrow={savedProp ? "Property saved" : isEditing ? "Properties" : useQuickCreate ? "Add the essentials now. You can complete the rest later." : (stageMeta?.eyebrow || "Properties")}
      testid="add-property-drawer"
      zClass={preset?.onSaved ? "z-[95]" : "z-[90]"}
      width={1080}
      headerExtra={headerExtra}
      footer={footer}
      aside={
        savedProp
          ? null
          : useQuickCreate
            ? <NextStepsRail form={form} contacts={contacts} score={score} isLeasehold={isLeasehold} />
            : (!isEditing
                ? <InsightsPanel form={form} score={score} errors={errors} currentStep={step} properties={properties} />
                : null)
      }
    >
      {savedProp ? (
        <NextActionCards savedProp={savedProp} close={close} />
      ) : isEditing ? (
        <EditPropertyBody
          form={form}
          set={set}
          errors={{ ...errors, ...editErrors }}
          negotiators={negotiatorOptions}
          onPickNegotiator={pickNegotiator}
          activeDeal={activeDeal}
        />
      ) : useQuickCreate ? (
        <QuickCreateBody
          form={form}
          set={set}
          errors={errors}
          contacts={contacts}
          negotiators={negotiatorOptions}
          onPickNegotiator={pickNegotiator}
          onThumb={onThumb}
          onLookupAddress={() => toast.info("Address lookup", { description: "Postcode lookup will be wired to your address provider." })}
          onFindAddress={() => toast.info("Find address", { description: "Address autocomplete will be wired to your provider." })}
          lastSavedLabel={lastSavedLabel}
        />
      ) : (
        stageRenderers[step]?.()
      )}
    </Drawer>
  );
};
